import {AbstractResource} from 'vue3-starter/src/typescript/resource/AbstractResource';
import {ResourceParams} from 'vue3-starter/src/typescript/resource/resource';
import {HttpServiceResponse} from 'vue3-starter/src/typescript/http/http';
import {context} from 'vue3-starter/src/typescript/app-context/AppContext';
import {briefResourceBuilder} from '@/brief-hub/brief/BriefResourceBuilder';
import * as RawModel from '@/common/api/api-raw-types';
import {
    AbstractDraftStateUnion,
    BriefDetails,
    BriefManagerResource,
    BriefOrigin,
    BriefStatus,
    BriefSubmissionRequest,
    BriefType,
    DateAsString,
    FreelancerBriefRequest,
    FreelancerJobTitleBriefRequest,
    FreelancerSkillsBriefRequest,
    MissionDescriptionBriefRequest,
    WhenConditionsBriefRequest,
    WhereConditionsBriefRequest,
} from '@/common/api/api-raw-types';
import {StrategyBriefRequest} from './StrategyBriefResource';

type BriefRequest = StandardBriefRequest | StrategyBriefRequest;

type StandardBriefRequest =
    | FreelancerBriefRequest
    | FreelancerJobTitleBriefRequest
    | FreelancerSkillsBriefRequest
    | WhenConditionsBriefRequest
    | WhereConditionsBriefRequest
    | MissionDescriptionBriefRequest
    | BriefSubmissionRequest;

export default class BriefResource extends AbstractResource {
    protected static async updateBrief(payload: BriefRequest, url: string): Promise<HttpServiceResponse<BriefResource>> {
        const [data, error] = await context.httpService.post<RawModel.BriefResource>(url, payload);
        if (error || !data) {
            return [null, error];
        }
        return [briefResourceBuilder.build(data), error];
    }

    briefId: string;
    briefDetails: BriefDetails;
    company: RawModel.CompanyResource;
    identityId: string;
    companyId: string;
    origin: BriefOrigin;
    creationDate: DateAsString;
    launchedDate: DateAsString;
    archivedDate: DateAsString;
    currentState: BriefStatus;
    draftState: AbstractDraftStateUnion;
    briefManager?: BriefManagerResource;
    organization: RawModel.OrganizationResource;
    profileDetails: RawModel.ProfileDetailsResource;
    type: BriefType;
    submittable: boolean;

    constructor(params: ResourceParams) {
        super(params);
        this.briefId = params.briefId;
        this.briefDetails = params.briefDetails;
        this.company = params.company;
        this.origin = params.origin;
        this.identityId = params.identityId;
        this.companyId = params.companyId;
        this.creationDate = params.creationDate;
        this.launchedDate = params.launchedDate;
        this.archivedDate = params.archivedDate;
        this.currentState = params.currentState;
        this.draftState = params.draftState;
        this.briefManager = params.briefManager;
        this.organization = params.organization;
        this.profileDetails = params.profileDetails;
        this.type = params.type;
        this.submittable = params.submittable;
    }

    formattedAddress(): string | null {
        const {address} = this.briefDetails;
        if (address?.city && address?.countryCode) {
            return `${address.city}, ${address.countryCode}`;
        }
        return null;
    }

    async saveFreelancerProfile(payload: FreelancerBriefRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.freelancerProfileSection());
    }

    async saveFreelancerJobTitle(payload: FreelancerJobTitleBriefRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.freelancerJobTitleSection());
    }

    async saveFreelancerSkills(payload: FreelancerSkillsBriefRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.freelancerSkillsSection());
    }

    async saveWhenConditions(payload: WhenConditionsBriefRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.whenConditionsSection());
    }

    async saveWhereConditions(payload: WhereConditionsBriefRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.whereConditionsSection());
    }

    async saveMissionDescription(payload: MissionDescriptionBriefRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.missionDescriptionSection());
    }

    async submitBrief(payload: BriefSubmissionRequest): Promise<HttpServiceResponse<BriefResource>> {
        return BriefResource.updateBrief(payload, this.links.briefSubmission());
    }
}
